import React from 'react';

/**
 * One room on the housekeeping screen (Oct 2026). Shared by Housekeeping Live
 * and the reception board so a room always reads the same everywhere.
 */
export const HK = {
  forest: '#213D30', paper: '#F7F2E8', card: '#FFFDF8', rule: '#D9CFBA',
  muted: '#56645B', gold: '#B8963E', goldBg: '#F1E6C8', goldText: '#5E4B14',
  okBg: '#DCE8E0', okText: '#1F4D35', ok: '#1F6B45', red: '#A3361F', redBg: '#FBEDE8', amber: '#7A6220',
};

const TONE = {
  urgent: { color: HK.red, fontWeight: 800 },
  warn: { color: HK.amber, fontWeight: 700 },
  ok: { color: HK.ok, fontWeight: 700 },
  info: { color: HK.forest, fontWeight: 600 },
  muted: { color: HK.muted, fontWeight: 500 },
};

export function statusLabel(s, waiting) {
  if (s === 'done') return 'Clean ✓';
  if (s === 'progress') return 'Cleaning now';
  return waiting ? 'Waiting' : 'To do';
}

export default function HkRoomCard({ room, kind, onAdvance, onOpen, compact }) {
  const s = room.status || 'todo';
  const top = kind === 'next';
  const waiting = kind === 'wait';
  const urgent = s !== 'done' && room.why.some((w) => w.tone === 'urgent');
  const btn = s === 'todo' ? 'Start' : s === 'progress' ? 'Done' : 'Undo';
  const btnStyle = s === 'todo'
    ? { background: HK.forest, color: HK.paper }
    : s === 'progress' ? { background: HK.gold, color: '#1D2A22' } : { background: 'transparent', color: HK.muted, border: `1px solid #B9AE95` };
  const border = urgent ? `2px solid ${HK.red}` : top ? `2px solid ${HK.forest}` : `1px solid ${HK.rule}`;

  return (
    <div
      className="rounded-sm p-3 flex gap-3 items-start"
      style={{ background: waiting ? '#F3EEE3' : HK.card, border, opacity: kind === 'done' ? 0.6 : 1 }}
    >
      <div
        className="flex-none w-11 h-11 flex items-center justify-center rounded-sm text-xl font-extrabold"
        style={top ? { background: HK.forest, color: HK.paper }
          : kind === 'done' ? { background: HK.okBg, color: HK.okText }
            : { color: HK.muted, border: `2px solid ${HK.rule}` }}
      >
        {kind === 'done' ? '✓' : room.position || '–'}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <button type="button" onClick={onOpen} className="text-left flex items-baseline gap-2 flex-wrap">
          <span className="text-[28px] leading-7 font-bold" style={{ fontFamily: 'Alice, Georgia, serif', color: HK.forest }}>{room.room}</span>
          <span className="text-[13px]" style={{ color: HK.muted }}>{room.type}</span>
          {room.premium && <span className="px-1.5 py-0.5 rounded-sm text-[11px] font-extrabold" style={{ background: HK.gold, color: '#1D2A22' }}>★ PREMIUM</span>}
        </button>
        {room.why.slice(0, compact ? 2 : 6).map((w, i) => (
          <div key={i} className="text-[15px] leading-5" style={TONE[w.tone] || TONE.info}>{w.text}</div>
        ))}
        {room.extras && room.extras.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {room.extras.map((x) => (
              <span key={x} className="px-2.5 py-1 rounded-sm text-[15px] font-bold" style={{ background: HK.goldBg, color: HK.goldText }}>{x}</span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2.5 mt-0.5">
          <span
            className="text-[13px] font-bold px-2 py-0.5 rounded-sm"
            style={s === 'done' ? { background: HK.okBg, color: HK.okText } : s === 'progress' ? { background: HK.goldBg, color: HK.goldText } : { color: HK.muted, border: `1px solid ${HK.rule}` }}
          >
            {statusLabel(s, waiting)}
          </span>
          {room.cleaner && s !== 'todo' && <span className="text-[13px] font-semibold" style={{ color: HK.forest }}>{room.cleaner.split(' ')[0]}</span>}
          {room.inspected && <span className="text-[12px] font-bold" style={{ color: HK.ok }}>Checked ✓</span>}
          {onOpen && <button type="button" onClick={onOpen} className="text-sm underline" style={{ color: HK.muted }}>Details</button>}
          <div className="flex-1" />
          {onAdvance && !waiting && (
            <button type="button" onClick={onAdvance} className="min-w-[92px] min-h-[48px] px-4 rounded-sm text-[17px] font-extrabold" style={btnStyle}>
              {btn}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
